import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { readAccessList } from "../../api/access/accessApi";
import { changeInstallationSettings, readInstallationSettings, type InstallationSettings, type InstallationSettingsRecord } from "../../api/settingsApi";
import ProjectRulesFields from "../../projects/settings/rules/ProjectRulesFields";
import GroupChoiceField from "../../projects/settings/groups/GroupChoiceField";
import { personName } from "../../projects/settings/settingsTexts";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { StarterGroups } from "../access";
import EmptyState from "../../ui/feedback/EmptyState";
import HoverHint from "../../ui/feedback/HoverHint";
import LoadStatus from "../../ui/feedback/LoadStatus";
import Field, { type FieldControlProps } from "../../ui/forms/Field";
import OptionLabel from "../../ui/forms/OptionLabel";
import ReasonedChangeForm from "../../ui/forms/ReasonedChangeForm";
import Section from "../../ui/forms/Section";
import { FORM_COMPACT_CONTROL_CLASS } from "../../ui/styles/formStyles";

/**
 * `/administration/installation` (accounts.md ST-1): how many projects one person may
 * create, whether a creator curates, whether people may ask for access (GS-8), the groups new
 * accounts join, how long a deleted project can be restored, and what a new project starts
 * with: its starter groups, as the server says them, and its rules. Who may create projects and use
 * the server's keys are group permissions now (Administration › Groups, GS-2). Every change
 * needs a reason; the list below says who changed what, when and why.
 */
export default function InstallationSettingsPage() {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readInstallationSettings(signal), []);
  const { state, reload, refresh } = useLoad(load);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const current = state.value.at(-1);
  if (current === undefined) return <EmptyState>{t("administration.installation.none")}</EmptyState>;
  return (
    <div className="max-w-4xl space-y-6">
      <Section title={t("administration.installation.title")} hint={t("administration.installation.hint")}>
        <ReasonedChangeForm
          value={current.settings}
          onSave={async (settings, reason) => { await changeInstallationSettings(settings, reason); refresh(); }}
        >
          {(settings, onChange) => <InstallationFields settings={settings} onChange={onChange} />}
        </ReasonedChangeForm>
      </Section>
      <Section title={t("administration.installation.history")}>
        <ol className="app-divide-y text-sm">
          {[...state.value].reverse().map((record) => <RecordLine key={record.at} record={record} />)}
        </ol>
      </Section>
    </div>
  );
}

function InstallationFields({ settings, onChange }: { settings: InstallationSettings; onChange?: (settings: InstallationSettings) => void }) {
  const { t } = useTranslation();
  const limitId = useId();
  const restoreDaysId = useId();
  const readOnly = onChange === undefined;
  const loadGroups = useCallback((signal: AbortSignal) => readAccessList(null, signal), []);
  const { state: groups } = useLoad(loadGroups);
  // People's own groups: DMW's own ones hold everyone or are joined otherwise.
  const installationGroups = groups.status === "loaded" ? groups.value.groups.filter((group) => group.system === null) : [];
  return (
    <div className="space-y-4 text-sm">
      <Field id={limitId} label={t("administration.installation.memberProjectLimit")} hint={t("administration.installation.memberProjectLimitHint")} layout="inline">
        {(control) => (
          <input {...control} type="number" min={1} className={`${FORM_COMPACT_CONTROL_CLASS} max-w-28`} disabled={readOnly}
            value={settings.memberProjectLimit ?? ""}
            onChange={(event) => {
              const limit = Number.parseInt(event.target.value, 10);
              onChange?.({ ...settings, memberProjectLimit: Number.isInteger(limit) && limit >= 1 ? limit : null });
            }} />
        )}
      </Field>
      <OptionLabel>
        <input type="checkbox" checked={settings.creatorCurates} disabled={readOnly}
          onChange={(event) => { onChange?.({ ...settings, creatorCurates: event.target.checked }); }} />
        {t("administration.installation.creatorCurates")}
      </OptionLabel>
      <div className="flex items-center gap-1">
        <OptionLabel>
          <input type="checkbox" checked={settings.accessRequests} disabled={readOnly}
            onChange={(event) => { onChange?.({ ...settings, accessRequests: event.target.checked }); }} />
          {t("administration.installation.accessRequests")}
        </OptionLabel>
        <HoverHint name={t("administration.installation.accessRequests")}>{t("administration.installation.accessRequestsHint")}</HoverHint>
      </div>
      <GroupChoiceField legend={t("administration.installation.newAccountGroups")} hint={t("administration.installation.newAccountGroupsHint")}
        none={t("administration.installation.noGroups")} groupIds={settings.newAccountGroups}
        groups={installationGroups}
        onChange={readOnly ? undefined : (newAccountGroups) => { onChange({ ...settings, newAccountGroups }); }} />
      <Field id={restoreDaysId} label={t("administration.installation.deletedProjectRestoreDays")} hint={t("administration.installation.deletedProjectRestoreDaysHint")} layout="inline">
        {(control) => (
          <DaysInput control={control} days={settings.deletedProjectRestoreDays} disabled={readOnly}
            onChange={(days) => { onChange?.({ ...settings, deletedProjectRestoreDays: days }); }} />
        )}
      </Field>
      <div className="space-y-2">
        <p className="app-title font-semibold">{t("administration.installation.newProjects")}</p>
        <StarterGroups itemKinds={groups.status === "loaded" ? groups.value.itemKinds : []} />
        {/* The note belongs to the defaults one changes here, not to the fixed starter groups above (Sol critique C5). */}
        <p className="app-help-text">{t("administration.installation.newProjectsHint")}</p>
        <ProjectRulesFields rules={settings.newProjects} onChange={onChange === undefined ? undefined : (rules) => { onChange({ ...settings, newProjects: rules }); }} />
      </div>
    </div>
  );
}

/** A whole number of days. What is typed shows as typed until it is one, so the field can be
 * cleared and typed afresh; leaving it shows the number in force again. */
function DaysInput({ control, days, disabled, onChange }: { control: FieldControlProps; days: number; disabled: boolean; onChange: (days: number) => void }) {
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <input {...control} type="number" className={`${FORM_COMPACT_CONTROL_CLASS} max-w-28`} disabled={disabled}
      value={typed ?? String(days)}
      onBlur={() => { setTyped(null); }}
      onChange={(event) => {
        const text = event.target.value;
        if (/^\d+$/.test(text)) {
          setTyped(null);
          onChange(Number.parseInt(text, 10));
        } else {
          setTyped(text);
        }
      }} />
  );
}

function RecordLine({ record }: { record: InstallationSettingsRecord }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="space-y-0.5 py-2">
      <p>
        {record.actor === null
          ? t("administration.installation.firstStart")
          : t("administration.installation.changed", { names: record.changed.map((name) => t(`administration.installation.names.${name}`)).join(", ") })}
      </p>
      {record.reason === "" ? null : <p className="text-xs">{t("permissionHistory.reason", { reason: record.reason })}</p>}
      <p className="app-text-muted text-xs">
        {formatDateTime(record.at, i18n.language)}
        {record.actor === null ? null : `, ${t("permissionHistory.by", { name: personName(t, record.actor) })}`}
      </p>
    </li>
  );
}
